/**
 * Tracking QA (born Video QA) — Priori staff only. The match video beside the tracking at
 * the same frame, Match Lab's stepping grammar, the detectors' findings
 * (video-qa/analysis) and staff notes (the existing game-notes API).
 * The URL carries ?frame= and ?finding=, so a copied link IS the share.
 *
 * The screen's clock runs KICKOFF to FINAL (periods[0].start to the
 * last period's end): warmup and aftermath are not shown. The top
 * timeline marks Start / HT / 2H / Final with 5-minute ticks, and a
 * bucket strip grades each 5 minutes green/yellow/red from the
 * findings (Ben, 2026-10-28).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { api, ApiError } from '../lib/api'
import { NO_FRAME_RATE_NOTE, servedFps } from '../lib/frameRate'
import {
  detectFramesIncludeGap, frameToVideoTime, syncFieldsOf, videoTimeToFrame,
  type SyncState,
} from '../lib/viz/frameTime'
import {
  GOAL_AREA_HALF_WIDTH_M, PEN_AREA_HALF_WIDTH_M,
} from '../lib/pitchZones'
import { TrackingChunks } from './trackingChunks'
import '../pages/admin.css'
import './video-qa.css'

type Period = { period_id: number; start_frame_idx: number; end_frame_idx: number }
type Info = {
  game_id: number; game_code?: string | null
  home_team: string | null; away_team: string | null
  fps: number | null; total_frames: number | null
  home_team_color: string | null; away_team_color: string | null
  periods: Period[]
  video_game_start_time?: number | null
  video_halftime_start_time?: number | null
  video_halftime_end_time?: number | null
}
export type Finding = {
  detector: string; severity: 'defect' | 'note'
  start_frame: number; end_frame: number; measure: number; text: string
}
type Analysis = { findings?: Finding[]; computed_in_ms?: number
                  vendor?: string | null
                  kickoff_frame?: number | null
                  keepers?: { home: number[]; away: number[] } | null
                  r2?: { tracking?: string | null
                         video?: string | null } | null
                  docs?: Record<string, { what: string; bar: string
                                          means: string
                                          investigate?: string }>
                  status?: 'ready' | 'computing' | 'failed'
                  error?: string | null
                  // why the frame-rate detectors were not measured
                  fps_note?: string | null
                  job?: { job_id?: string | null
                          completed_at?: string | null
                          file_modified_at?: string | null } | null }
type Note = { id: number; body: string; by?: string | null; at?: string | null
              removed?: boolean }
type GameListItem = {
  game_id: number; game_code?: string | null
  home_team: string | null; away_team: string | null
  match_date: string | null; vendors: string[]
  organization_id?: number | null
}
type OpsOrganization = { id: number; name: string; code: string }

const CHUNK = 1800
/** One analysis bucket on the strip, seconds of match time. */
export const BUCKET_S = 300
/** Every detector the analysis runs, in rail order. */
export const DETECTORS = ['dead_opening', 'ball_teleport', 'ball_adrift',
                          'too_many_players', 'too_few_players',
                          'duplicate_jersey', 'homography_jump',
                          'live_flapping', 'missing_keeper'] as const

/** `[frame 123] text` — the composer writes it, the list links it. */
export function noteFrame(body: string): { frame: number | null; text: string } {
  const m = /^\[frame (\d+)\]\s*/.exec(body)
  return m ? { frame: Number(m[1]), text: body.slice(m[0].length) }
    : { frame: null, text: body }
}

/** The timeline's labelled ticks: Match Start, 5…45, HT, 2ND, 50…90,
 *  FT — match minutes from each half's kickoff. Minute labels that
 *  would crowd a milestone (HT running to exactly 45', FT at 90') are
 *  dropped so the milestones stay readable. */
export type TimelineLabel =
  { frame: number; text: string; align?: 'start' | 'end' }

export function timelineLabels(kick: number, ht: number | null,
                               h2: number | null, final: number,
                               fps: number): TimelineLabel[] {
  const span = Math.max(1, final - kick)
  const miles = [kick, ht, h2, final].filter((x): x is number => x != null)
  const clear = (f: number) =>
    miles.every((mf) => Math.abs(f - mf) / span >= 0.02)
  const out: TimelineLabel[] = [{ frame: kick, text: 'Match Start' }]
  for (let m = 5; m <= 45; m += 5) {
    const f = kick + m * 60 * fps
    if (f < (ht ?? final) && clear(f)) out.push({ frame: f, text: String(m) })
  }
  // HT sits BEFORE the break (right-aligned to the first half's end),
  // 2ND AFTER it (left-aligned to the second half's start) — so frame-
  // contiguous halves still show both, flanking the same tick.
  if (ht != null) out.push({ frame: ht, text: 'HT', align: 'end' })
  if (h2 != null) {
    out.push({ frame: h2, text: '2ND', align: 'start' })
    for (let m = 50; m <= 90; m += 5) {
      const f = h2 + (m - 45) * 60 * fps
      if (f < final && clear(f)) out.push({ frame: f, text: String(m) })
    }
  }
  out.push({ frame: final, text: 'FT' })
  return out
}

/** Canvas y for a tracking y (centimetres from the centre spot).
 *  Canvas y grows DOWN, pitch +y points up — the sign flip matches
 *  Match Lab's canvasController. With a plus, the pitch mirrored
 *  laterally against the video: attack direction kept, the team's
 *  left and right sides swapped (Ben, game 570, 2026-10-29). */
export function canvasY(cm: number, H: number): number {
  return H / 2 - (cm / 100) * (H / 78)
}

/** 'YYYY-MM-DD HH:MM <tz>' from an ISO stamp — the header's job/file
 *  dates; sub-minute precision is noise there, but the zone is not
 *  (Ben, 2026-10-29). Our stamps are UTC; a non-UTC offset shows
 *  as-is rather than being silently dropped. */
export function stampDay(iso: string): string {
  const m = iso.match(/(Z|[+-]\d{2}:\d{2})$/)
  const tz = !m || m[1] === 'Z' || m[1] === '+00:00' ? 'UTC' : m[1]
  return `${iso.slice(0, 16).replace('T', ' ')} ${tz}`
}

/** A finding's stable identity for keys, links and highlights:
 *  detector+frame alone collide (both missing_keeper notes sit at
 *  frame 0 — SDET review of #1018), so the payload index joins in. */
export function findingKey(f: Finding, i: number): string {
  return `${f.detector}:${f.start_frame}:${i}`
}

/** The timeline/seek domain start: kickoff, stretched back ONLY by a
 *  dead_opening (which by definition starts before the first live
 *  ball, at the period mark). Other frame-0 findings — missing_keeper
 *  is a game-wide note pinned at 0 — must NOT drag the whole domain
 *  back to frame 0 (SDET review of #1018: it offset every bucket by
 *  the pre-kickoff stretch). */
export function domainStart(findings: Finding[], kick: number): number {
  return findings
    .filter((f) => f.detector === 'dead_opening'
      && !f.text.includes('not measured'))
    .reduce((lo, f) => Math.min(lo, f.start_frame), kick)
}

/** Piecewise frame↔x mapping that opens the LABELLED halftime break as
 *  real width: tracking frames usually run contiguously through the
 *  break, but the labelled video times know it lasted ~15 minutes — so
 *  HT and 2ND separate on the bar instead of touching. gapFrames is the
 *  break's length in frames (0 when the frame indices already carry
 *  it). Clicking inside the opened break snaps to 2ND. */
export function timelineDomain(start: number, final: number,
                               ht: number | null, h2: number | null,
                               gapFrames: number) {
  const gap = ht != null && h2 != null && gapFrames > 0 ? gapFrames : 0
  const span = Math.max(1, final + gap - start)
  const disp = (f: number) => (gap && h2 != null && f >= h2 ? f + gap : f)
  const toX = (f: number) =>
    Math.min(1, Math.max(0, (disp(f) - start) / span))
  const fromX = (x: number) => {
    const df = start + Math.min(1, Math.max(0, x)) * span
    if (gap && ht != null && h2 != null && df > ht) {
      return df < h2 + gap ? h2 : df - gap
    }
    return df
  }
  return { toX, fromX }
}

/** 'green' | 'yellow' | 'red' per BUCKET_S bucket of the domain: the
 *  worst finding severity whose span touches the bucket. */
export function bucketStatuses(findings: Finding[], kick: number,
                               final: number, fps: number): string[] {
  const bucketFrames = BUCKET_S * fps
  const n = Math.max(1, Math.ceil((final - kick) / bucketFrames))
  const out = new Array<string>(n).fill('green')
  for (const f of findings) {
    if (f.text.includes('not measured')) continue
    const a = Math.max(0, Math.floor((f.start_frame - kick) / bucketFrames))
    const b = Math.min(n - 1, Math.floor((f.end_frame - kick) / bucketFrames))
    for (let i = a; i <= b && i < n; i++) {
      if (f.severity === 'defect') out[i] = 'red'
      else if (out[i] === 'green') out[i] = 'yellow'
    }
  }
  return out
}

/** The rail's per-detector state: worst severity, count, or unmeasured. */
export function detectorSummary(findings: Finding[]) {
  return DETECTORS.map((d) => {
    // A fail-closed "not measured" note greys the detector; it is not
    // a finding about the game (SDET review of #1018 — it counted as
    // yellow, leaving the grey state unreachable).
    const mine = findings.filter(
      (f) => f.detector === d && !f.text.includes('not measured'))
    const unmeasured = findings.some(
      (f) => f.detector === d && f.text.includes('not measured'))
    const status = mine.some((f) => f.severity === 'defect') ? 'red'
      : mine.length ? 'yellow' : unmeasured ? 'grey' : 'green'
    return { detector: d, status, count: mine.length }
  })
}

export default function VideoQA() {
  const { gameId: gameIdParam } = useParams()
  const gameId = Number(gameIdParam) || 0
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [games, setGames] = useState<GameListItem[]>([])
  const [orgs, setOrgs] = useState<OpsOrganization[]>([])
  const [clubId, setClubId] = useState('')
  const [info, setInfo] = useState<Info | null>(null)
  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  const [videoError, setVideoError] = useState<string | null>(null)
  const [analysis, setAnalysis] = useState<Analysis | null>(null)
  const [analysisError, setAnalysisError] = useState<string | null>(null)
  const [notes, setNotes] = useState<Note[]>([])
  const [noteDraft, setNoteDraft] = useState('')
  const [copied, setCopied] = useState<string | null>(null)
  const [rate, setRate] = useState(1.0)
  const [colors, setColors] = useState(() => {
    try {
      return { home: '', away: '', ball: '#ffe14d',
               ...JSON.parse(localStorage.getItem('vqa-colors') || '{}') }
    } catch { return { home: '', away: '', ball: '#ffe14d' } }
  })
  const setColor = (k: 'home' | 'away' | 'ball') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = { ...colors, [k]: e.target.value }
      setColors(next)
      localStorage.setItem('vqa-colors', JSON.stringify(next))
    }
  const [frame, setFrameRaw] = useState(() => Number(params.get('frame')) || 0)
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const frameRef = useRef(0)

  const recordedFps = servedFps(info?.fps)
  // null until match-lab-info answers, and for a game with no recorded
  // rate: nothing is placed in time without one
  const fps = recordedFps
  const noRate = info != null && fps === null
  const live = gameId > 0 && fps !== null
  const periods = useMemo(() => info?.periods ?? [], [info])
  // The REAL kickoff is the first live ball: trdaas period marks are
  // phase bounds and can open on the anthem (KNOWLEDGE 47).
  const kick = analysis?.kickoff_frame
    ?? periods[0]?.start_frame_idx ?? 0
  const final = periods[periods.length - 1]?.end_frame_idx
    ?? info?.total_frames ?? 0
  const tlStart = useMemo(
    () => domainStart(analysis?.findings ?? [], kick), [analysis, kick])
  const activeFinding = params.get('finding')

  const sync: SyncState | null = useMemo(() => {
    if (fps === null) return null
    const base: SyncState = {
      videoGameStartTime: info?.video_game_start_time ?? 0,
      videoHalftimeStartTime: info?.video_halftime_start_time ?? null,
      videoHalftimeEndTime: info?.video_halftime_end_time ?? null,
      ...syncFieldsOf(info as never),
      fps,
    }
    const h2 = periods.find((p) => p.period_id === 2)
    return { ...base, framesIncludeHalftimeGap:
      detectFramesIncludeGap(base, h2?.start_frame_idx ?? null) }
  }, [info, fps, periods])
  const syncRef = useRef(sync)
  syncRef.current = sync
  const domainRef = useRef({ start: tlStart, final })
  domainRef.current = { start: tlStart, final }

  const chunks = useMemo(() => new TrackingChunks(
    (start, count) =>
      `/api/v1/opsqa/games/${gameId}/tracking-frames/${start}/${count}`,
    CHUNK), [gameId])

  /** Move the cursor: clamped to the match, URL and video follow. */
  const setFrame = useCallback((f: number, seekVideo = true) => {
    const { start: k, final: e } = domainRef.current
    const clamped = Math.min(Math.max(Math.round(f), k), e || Math.round(f))
    frameRef.current = clamped
    setFrameRaw(clamped)
    setParams((p) => {
      const n = new URLSearchParams(p)
      n.set('frame', String(clamped))
      return n
    }, { replace: true })
    const v = videoRef.current
    const s = syncRef.current
    if (seekVideo && v && s) {
      v.pause()
      v.currentTime = Math.max(0, frameToVideoTime(s, clamped))
    }
  }, [setParams])

  useEffect(() => {
    api<{ games: GameListItem[] }>('/api/v1/opsqa/games')
      .then((d) => setGames(d.games ?? [])).catch(() => {})
    api<{ organizations: OpsOrganization[] }>('/api/v1/opsqa/organizations')
      .then((d) => setOrgs(d.organizations ?? [])).catch(() => {})
  }, [])
  useEffect(() => {
    if (!gameId) return
    setInfo(null)
    setFrameRaw(0)
    frameRef.current = 0
    setParams((p) => {
      const n = new URLSearchParams(p)
      n.delete('finding')
      return n
    }, { replace: true })
    api<Info>(`/api/v1/opsqa/games/${gameId}/match-lab-info`)
      .then(setInfo).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps -- on game switch
  }, [gameId])
  // The match starts at kickoff: land there, not on frame 0 (and not on
  // the previous game's cursor). When the analysis later reports the
  // first LIVE ball past the period mark, the start moves up to it.
  useEffect(() => {
    if (!info) return
    const k = info.periods?.[0]?.start_frame_idx ?? 0
    const asked = Number(params.get('frame'))
    setFrame(frameRef.current === 0 && asked > k ? asked : k)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- on info load only
  }, [info])
  useEffect(() => {
    const k = analysis?.kickoff_frame
    if (k != null && frameRef.current < k) setFrame(k)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- on analysis
  }, [analysis?.kickoff_frame])
  useEffect(() => {
    if (!gameId) return
    setVideoUrl(null); setVideoError(null)
    api<{ video_url?: string | null; video_url_fallback?: string | null }>(
      `/api/v1/opsqa/games/${gameId}/video`)
      .then((d) => setVideoUrl(d.video_url ?? d.video_url_fallback ?? null))
      .catch((e: unknown) =>
        setVideoError(e instanceof ApiError ? e.message : String(e)))
  }, [gameId])
  // The analysis computes the whole match in a server thread: poll
  // until it reports ready (or failed), every 2.5 s.
  const [analysisNonce, setAnalysisNonce] = useState(0)
  useEffect(() => {
    if (!gameId) return
    setAnalysis(null); setAnalysisError(null)
    let stop = false
    let timer = 0
    let first = analysisNonce > 0
    let errDelay = 5000
    const tick = () => {
      const qs = first ? '?refresh=1' : ''
      first = false
      api<{ data?: Analysis } & Analysis>(
        `/api/v1/opsqa/games/${gameId}/video-qa/analysis${qs}`)
        .then((d) => {
          if (stop) return
          const a = (d as { data?: Analysis }).data ?? d
          setAnalysis(a)
          setAnalysisError(null)
          if (a.status === 'computing') {
            timer = window.setTimeout(tick, 2500)
          }
        })
        .catch((e: unknown) => {
          if (stop) return
          setAnalysisError(e instanceof ApiError ? e.message : String(e))
          // keep polling after a TRANSIENT failure (a 503 on the
          // first ask otherwise spins forever with no re-run button
          // to recover with) — with backoff, and never for a 403/404:
          // a revoked role or wrong game id is terminal (SDET review
          // of #1018, twice)
          const status = e instanceof ApiError ? e.status : 0
          if (status !== 403 && status !== 404) {
            timer = window.setTimeout(tick, errDelay)
            errDelay = Math.min(errDelay * 2, 60_000)
          }
        })
    }
    tick()
    return () => { stop = true; window.clearTimeout(timer) }
  }, [gameId, analysisNonce])
  const loadNotes = useCallback(() => {
    if (!gameId) return
    api<{ notes: Note[] }>(`/api/v1/opsqa/games/${gameId}/notes`)
      .then((d) => setNotes(d.notes ?? [])).catch(() => {})
  }, [gameId])
  useEffect(loadNotes, [loadNotes])

  // The video is the clock while playing: follow it, inside the match.
  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    const onTime = () => {
      const s = syncRef.current
      if (v.paused || !s) return
      const f = videoTimeToFrame(s, v.currentTime)
      const { final: e } = domainRef.current
      if (e && f > e) { v.pause(); return }
      setFrameRaw(f)
      setParams((p) => {
        const n = new URLSearchParams(p)
        n.set('frame', String(f))
        return n
      }, { replace: true })
    }
    v.addEventListener('timeupdate', onTime)
    return () => v.removeEventListener('timeupdate', onTime)
  }, [videoUrl, setParams])

  // Pitch canvas (camera view, centimetres from the centre spot).
  // Imperative: the rAF loop draws every video frame without a React
  // render; the effect below redraws on manual steps.
  const drawFrame = useCallback((f: number) => {
    if (fps === null) return
    chunks.ensure(f)
    chunks.ensure(f + CHUNK)
    // without pruning a full match accumulates every chunk ever
    // visited (~240k frames) — keep the same 3-chunk window Match Lab
    // keeps
    chunks.prune(f, 3)
    const c = canvasRef.current
    if (!c) return
    const ctx = c.getContext('2d')
    if (!ctx) return
    const W = c.width, H = c.height
    const sx = (cm: number) => W / 2 + (cm / 100) * (W / 115)
    const sy = (cm: number) => canvasY(cm, H)
    ctx.clearRect(0, 0, W, H)
    ctx.strokeStyle = '#3a4150'
    ctx.strokeRect(sx(-5250), sy(-3400), sx(5250) - sx(-5250),
                   sy(3400) - sy(-3400))
    ctx.beginPath()
    ctx.moveTo(sx(0), sy(-3400)); ctx.lineTo(sx(0), sy(3400)); ctx.stroke()
    // 18-yard and goal boxes — widths from lib/pitchZones (the ONE
    // module allowed to hold the channel lines, its own guard test),
    // depths from the Laws; centimetres from the spot. The circle is
    // an ellipse because x and y scale differently.
    const penW = PEN_AREA_HALF_WIDTH_M * 100
    const goalW = GOAL_AREA_HALF_WIDTH_M * 100
    for (const side of [-1, 1]) {
      const gl = side * 5250
      ctx.strokeRect(sx(gl), sy(-penW),
                     sx(gl - side * 1650) - sx(gl), sy(penW) - sy(-penW))
      ctx.strokeRect(sx(gl), sy(-goalW),
                     sx(gl - side * 550) - sx(gl), sy(goalW) - sy(-goalW))
    }
    ctx.beginPath()
    // ellipse() throws IndexSizeError on a negative radius, and canvas y
    // grows down while pitch +y points up (canvasY) — take magnitudes.
    ctx.ellipse(sx(0), sy(0), Math.abs(sx(915) - sx(0)),
                Math.abs(sy(915) - sy(0)), 0, 0, Math.PI * 2)
    ctx.stroke()
    const fr = chunks.frames.get(f)
      ?? chunks.frames.get(f - (f % Math.max(1, Math.round(fps / 5))))
    if (!fr) {
      ctx.fillStyle = '#8a93a5'
      ctx.fillText(chunks.isLoaded(f)
        ? 'no tracking at this frame' : 'loading tracking\u2026', 12, 16)
      return
    }
    ctx.font = '600 9px sans-serif'
    ctx.textAlign = 'center'
    // Keepers get their own colour: purple unless a team is near
    // purple, then green (Ben, 2026-10-28).
    const hue = (hex: string) => {
      const n = parseInt(hex.replace('#', ''), 16)
      const r = (n >> 16) / 255, g = ((n >> 8) & 255) / 255,
        b = (n & 255) / 255
      const mx = Math.max(r, g, b), mn = Math.min(r, g, b)
      if (mx === mn) return -1
      const d = mx - mn
      const h = mx === r ? (g - b) / d + (g < b ? 6 : 0)
        : mx === g ? (b - r) / d + 2 : (r - g) / d + 4
      return h * 60
    }
    const homeFill = colors.home || info?.home_team_color || '#4da3ff'
    const awayFill = colors.away || info?.away_team_color || '#ff6b6b'
    const purplish = [homeFill, awayFill].some((c) => {
      const h = hue(c); return h >= 250 && h <= 310
    })
    const keeperFill = purplish ? '#2fbf4f' : '#a05ce0'
    const keepers = analysis?.keepers
    for (const p of fr.players) {
      const side = p.team === 1 ? 'home' : 'away'
      const isKeeper = !!keepers?.[side]?.includes(p.jersey)
      const fill = isKeeper ? keeperFill
        : p.team === 1 ? homeFill : awayFill
      ctx.fillStyle = fill
      ctx.beginPath()
      ctx.arc(sx(p.x), sy(p.y), 5.5, 0, Math.PI * 2)
      ctx.fill()
      if (p.jersey) {
        ctx.strokeStyle = 'rgba(0,0,0,0.75)'
        ctx.lineWidth = 2.5
        ctx.strokeText(String(p.jersey), sx(p.x), sy(p.y) - 8)
        ctx.fillStyle = '#ffffff'
        ctx.fillText(String(p.jersey), sx(p.x), sy(p.y) - 8)
      }
    }
    if (fr.ball) {
      ctx.fillStyle = colors.ball || '#ffe14d'
      ctx.beginPath()
      ctx.arc(sx(fr.ball.x), sy(fr.ball.y), 3.5, 0, Math.PI * 2)
      ctx.fill()
    }
  }, [chunks, fps, info, colors, analysis?.keepers])

  useEffect(() => {
    if (gameId) drawFrame(frame)
  }, [frame, drawFrame, gameId])

  // While the video plays, draw at the DISPLAY rate from the video's own
  // clock (the frame state only updates ~4 Hz on timeupdate, which read
  // as stutter); the React cursor follows at ~4 Hz for the URL/clock.
  useEffect(() => {
    const v = videoRef.current
    if (!v) return
    let raf = 0
    let last = 0
    const loop = (now: number) => {
      const s = syncRef.current
      if (s && !v.paused && !v.ended) {
        const f = videoTimeToFrame(s, v.currentTime)
        drawFrame(f)
        if (now - last > 250) {
          last = now
          frameRef.current = f
          setFrameRaw(f)
        }
      }
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [videoUrl, drawFrame])

  // Match Lab's stepping grammar.
  useEffect(() => {
    if (fps === null) return
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA'
                || t.tagName === 'SELECT')) return
      const v = videoRef.current
      const steps: Record<string, number> = {
        ',': -1, '.': 1,
        ArrowLeft: e.shiftKey ? -10 * fps : -fps,
        ArrowRight: e.shiftKey ? 10 * fps : fps,
      }
      const step = steps[e.key]
      if (step !== undefined) {
        e.preventDefault()
        setFrame(frame + step)
      } else if (e.key === ' ') {
        e.preventDefault()
        if (v) { if (v.paused) void v.play(); else v.pause() }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [frame, fps, setFrame])

  const share = useCallback((finding?: Finding, key?: string) => {
    const u = new URL(window.location.href)
    const f = finding ? finding.start_frame : frame
    u.searchParams.set('frame', String(f))
    if (finding && key) {
      u.searchParams.set('finding', key)
    } else {
      u.searchParams.delete('finding')
    }
    void navigator.clipboard?.writeText(u.toString())
    setCopied(key ?? 'cursor')
    window.setTimeout(() => setCopied(null), 1500)
  }, [frame])

  const postNote = useCallback(() => {
    const text = noteDraft.trim()
    if (!text) return
    void api(`/api/v1/opsqa/games/${gameId}/notes`, {
      method: 'POST',
      body: JSON.stringify({ body: `[frame ${frame}] ${text}` }),
    }).then(() => { setNoteDraft(''); loadNotes() })
  }, [noteDraft, frame, gameId, loadNotes])

  const findings = analysis?.findings ?? []
  const computing = !analysis || analysis.status === 'computing'
  const buckets = useMemo(
    () => (fps !== null && final > tlStart
      ? bucketStatuses(findings, tlStart, final, fps) : []),
    [findings, tlStart, final, fps])
  const summary = useMemo(() => {
    const s = detectorSummary(findings)
    return (!analysis || (analysis.status === 'computing'
                          && !findings.length))
      ? s.map((d) => ({ ...d, status: 'grey' })) : s
  }, [findings, analysis])
  const mmss = useCallback((f: number) => {
    if (fps === null) return null
    const t = Math.max(0, (f - kick) / fps)
    return `${Math.floor(t / 60)}:${String(
      Math.floor(t % 60)).padStart(2, '0')}`
  }, [kick, fps])
  const clock = mmss(frame)
  // each finding names its own window in match time — the rail is
  // whole-match, and 'around frame N' alone left the scope unclear
  // (Ben, 2026-10-29)
  const findingWhen = (f: Finding) =>
    f.end_frame > f.start_frame
      ? `${mmss(f.start_frame)}–${mmss(f.end_frame)}`
      : mmss(f.start_frame)
  const game = games.find((g) => g.game_id === gameId)
  useEffect(() => {
    if (game?.organization_id != null && !clubId) {
      setClubId(String(game.organization_id))
    }
  }, [game, clubId])
  const matches = games.filter((g) => String(g.organization_id) === clubId)
  const videoExt = videoUrl
    ? (videoUrl.split('?')[0]!.split('.').pop() ?? '').toUpperCase() : null
  // Where the first half ENDS and the second STARTS come from the
  // LABELLED video times, not the period marks: trdaas marks are phase
  // bounds, and period 1's end mark can swallow the whole break — a
  // "60+ minute first half" with HT touching 2ND (Ben, 2026-10-28).
  // Marks are the fallback when halftime was never labelled.
  const htLab = info?.video_halftime_start_time
  const h2Lab = info?.video_halftime_end_time
  const labelled = htLab != null && h2Lab != null && h2Lab > htLab
  const ht = labelled && sync ? videoTimeToFrame(sync, htLab)
    : periods[0]?.end_frame_idx ?? null
  const h2 = labelled && sync ? videoTimeToFrame(sync, h2Lab)
    : periods.find((p) => p.period_id === 2)?.start_frame_idx ?? null
  const tlabels = fps === null ? [] : timelineLabels(kick, ht, h2, final, fps)
  // the labelled break's length in frames; 0 when the frame indices
  // already include it (then the gap is real frame distance)
  const breakGap = (sync && fps !== null && !sync.framesIncludeHalftimeGap
                    && labelled)
    ? Math.round((h2Lab - htLab) * fps) : 0
  const dom = timelineDomain(tlStart, final, ht, h2, breakGap)

  return (
    <div className="vqa">
      <header className="vqa__head">
        <h2 className="vqa__title">Tracking QA</h2>
        <select className="adm-select" aria-label="Club" value={clubId}
                onChange={(e) => setClubId(e.target.value)}>
          <option value="">Pick a club</option>
          {orgs.map((o) => (
            <option key={o.id} value={String(o.id)}>{o.name}</option>
          ))}
        </select>
        <select className="adm-select" aria-label="Match"
                value={gameId ? String(gameId) : ''} disabled={!clubId}
                onChange={(e) => navigate(`/ops/tracking-qa/${e.target.value}`)}>
          <option value="" disabled>Pick a match</option>
          {matches.map((g) => (
            <option key={g.game_id} value={String(g.game_id)}>
              {g.game_code}{g.match_date ? ` · ${g.match_date}` : ''} · #{g.game_id}
            </option>
          ))}
        </select>
        {gameId > 0 && (
          <>
            <span className="t-label">
              {(analysis?.vendor ?? game?.vendors?.join(', ')) || '?'} tracking
              {videoExt ? ` · ${videoExt} video` : ''}
              {recordedFps !== null ? ` · ${recordedFps} fps` : ''}
            </span>
            <span className="t-label">frame {frame}{clock !== null ? ` · ${clock}` : ''}</span>
            {analysis?.job?.job_id && (
              <span className="vqa__meta vqa__jobline"
                    title={'the TRDAAS job behind this tracking; a '
                      + 're-derive replaces the parquet without a new '
                      + 'job, so the file date can be newer'}>
                job {analysis.job.job_id}
                {analysis.job.completed_at
                  ? ` · finalized ${stampDay(analysis.job.completed_at)}` : ''}
                {analysis.job.file_modified_at
                  ? ` · file ${stampDay(analysis.job.file_modified_at)}` : ''}
              </span>)}
            {analysis?.r2?.tracking && (
              <a className="vqa__r2link" href={analysis.r2.tracking}
                 target="_blank" rel="noreferrer">tracking in R2</a>)}
            {analysis?.r2?.video && (
              <a className="vqa__r2link" href={analysis.r2.video}
                 target="_blank" rel="noreferrer">video in R2</a>)}
            <a className="vqa__r2link"
               href={`/ops/layer-stack?view=match&game=${gameId}`}>
              layer stack</a>
            <a className="vqa__r2link" href={`/ops/match-lab/${gameId}`}>
              match lab</a>
            <button className="adm-btn adm-btn--quiet" onClick={() => share()}>
              {copied === 'cursor' ? 'Link copied ✓' : 'Share this moment'}
            </button>
          </>
        )}
      </header>

      {gameId > 0 && noRate && (
        <p className="muted" role="note">{NO_FRAME_RATE_NOTE}</p>
      )}
      {gameId > 0 && info == null && (
        <p className="muted" role="status">Loading the match…</p>
      )}

      {live && (
      <div className="vqa__timeline"
           onClick={(e) => {
             const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
             setFrame(dom.fromX((e.clientX - r.left) / r.width))
           }}>
        <div className="vqa__buckets">
          {buckets.map((s, i) => {
            const a = tlStart + i * BUCKET_S * fps
            const b = Math.min(final, a + BUCKET_S * fps)
            const left = dom.toX(a) * 100
            return (
              <div key={i} className={`vqa__bucket vqa__bucket--${s}`}
                   style={{ left: `${left}%`,
                            width: `${dom.toX(b) * 100 - left}%` }}
                   title={`${Math.floor(i * BUCKET_S / 60)}'–${
                     Math.floor((i + 1) * BUCKET_S / 60)}'`} />
            )
          })}
        </div>
        {tlabels.map((l) => {
            const x = dom.toX(l.frame)
            return (
              <button key={`${l.text}:${l.frame}`} className="vqa__tlabel"
                      title={`go to ${l.text}`}
                      style={x < 0.02 || l.align === 'start'
                        ? { left: `${x * 100}%`, transform: 'none' }
                        : x > 0.98 || l.align === 'end'
                          ? { right: `${(1 - x) * 100}%`,
                              transform: 'none' }
                          : { left: `${x * 100}%` }}
                      onClick={(e) => { e.stopPropagation(); setFrame(l.frame) }}>
                {l.text}
              </button>
            )
          })}
        {tlabels.map((l) => (
          <div key={`t:${l.frame}`} className="vqa__tick"
               style={{ left: `${dom.toX(l.frame) * 100}%` }} />
        ))}
        {findings.filter((f) => !f.text.includes('not measured')).map((f, i) => (
          <div key={`${f.detector}:${f.start_frame}:${i}`}
               className={`vqa__mark vqa__mark--${f.severity}`}
               title={f.text}
               style={{ left: `${dom.toX(f.start_frame) * 100}%` }} />
        ))}
        <div className="vqa__cursor"
             style={{ left: `${dom.toX(frame) * 100}%` }} />
      </div>
      )}

      {live && (
      <div className="vqa__transport">
        {[-30, -10, -1].map((s) => (
          <button key={s} className="adm-btn adm-btn--quiet"
                  onClick={() => setFrame(frame + s * fps)}>
            {'\u2039'}{-s}s</button>
        ))}
        <button className="adm-btn adm-btn--quiet" title="back one frame"
                onClick={() => setFrame(frame - 1)}>{'\u25C2'}</button>
        <button className="adm-btn vqa__play" title="play / pause (space)"
                onClick={() => {
                  const v = videoRef.current
                  if (v) { if (v.paused) void v.play(); else v.pause() }
                }}>{'\u25B6'}</button>
        <button className="adm-btn adm-btn--quiet" title="forward one frame"
                onClick={() => setFrame(frame + 1)}>{'\u25B8'}</button>
        {[1, 10, 30].map((s) => (
          <button key={s} className="adm-btn adm-btn--quiet"
                  onClick={() => setFrame(frame + s * fps)}>
            {s}s{'\u203A'}</button>
        ))}
        <select className="adm-select vqa__rate" aria-label="Playback rate"
                value={String(rate)}
                onChange={(e) => {
                  const r = Number(e.target.value)
                  setRate(r)
                  if (videoRef.current) videoRef.current.playbackRate = r
                }}>
          {[0.25, 0.5, 1, 2, 4].map((r) => (
            <option key={r} value={String(r)}>{r}x</option>
          ))}
        </select>
      </div>
      )}

      {live && (
      <div className="vqa__panels">
        <div className="vqa__video">
          {videoUrl
            ? <video ref={videoRef} src={videoUrl} controls preload="auto"
                     muted
                     onPlay={(e) => { e.currentTarget.playbackRate = rate }}
                     onLoadedMetadata={(e) => {
                       const s = syncRef.current
                       if (s) e.currentTarget.currentTime = Math.max(
                         0, frameToVideoTime(s, frameRef.current))
                     }} />
            : <p className="vqa__empty">
                {videoError ? `No video: ${videoError}`
                  : 'Video unavailable — tracking-only mode'}</p>}
        </div>
        <div className="vqa__pitch">
          <canvas ref={canvasRef} width={575} height={390} />
          <div className="vqa__colors">
            <label>{info?.home_team ?? 'home'} (H) <input type="color"
              aria-label="Home colour"
              value={colors.home || info?.home_team_color || '#4da3ff'}
              onChange={setColor('home')} /></label>
            <label>{info?.away_team ?? 'away'} (A) <input type="color"
              aria-label="Away colour"
              value={colors.away || info?.away_team_color || '#ff6b6b'}
              onChange={setColor('away')} /></label>
            <label>ball <input type="color" aria-label="Ball colour"
              value={colors.ball} onChange={setColor('ball')} /></label>
          </div>
        </div>
      </div>
      )}

      {live && (
      <aside className="vqa__rail">
          <section>
            <h3 className="t-label">Analysis — whole match (the timeline strip
              grades 5-minute buckets)</h3>
            {computing && (
              <p className="vqa__empty">analyzing the whole match…
                {findings.length > 0 ? ' (showing the last run)' : ''}</p>)}
            {analysis?.status === 'failed' && (
              <p className="vqa__empty">the analysis failed
                {analysis.error ? `: ${analysis.error}` : ' — re-run it '
                  + 'to see why'}</p>)}
            {analysis?.fps_note && (
              <p className="vqa__empty">{analysis.fps_note}</p>)}
            <ul className="vqa__summary">
              {summary.map((s) => {
                const doc = analysis?.docs?.[s.detector]
                return (
                  <li key={s.detector}>
                    <details>
                      <summary>
                        <span className={`vqa__dot vqa__dot--${s.status}`} />
                        {s.detector.replace(/_/g, ' ')}
                        {s.count > 0 ? ` (${s.count})` : ''}
                        {s.status === 'grey' ? ' — not measured' : ''}
                      </summary>
                      {doc && (
                        <div className="vqa__doc">
                          <p>{doc.what}</p>
                          <p>{doc.bar}</p>
                          <p>{doc.means}</p>
                        </div>
                      )}
                    </details>
                  </li>
                )
              })}
            </ul>
            {analysisError && <p className="vqa__empty">{analysisError}</p>}
            {analysis?.status === 'ready' && findings.length === 0 && (
              <p className="vqa__empty">No findings — the tracking looks
                clean to every detector.</p>)}
            <ul className="vqa__findings">
              {findings.map((f, i) => {
                const key = findingKey(f, i)
                return (
                  <li key={key}
                      className={`vqa__finding vqa__finding--${f.severity}`
                        + (activeFinding === key ? ' vqa__finding--active' : '')}>
                    <button onClick={() => setFrame(f.start_frame)}>
                      <span className="vqa__when">{findingWhen(f)}</span>
                      {' '}{f.text}
                    </button>
                    <button className="vqa__copy" title="Copy link to this finding"
                            onClick={() => share(f, key)}>
                      {copied === key ? '✓' : 'link'}
                    </button>
                  </li>
                )
              })}
            </ul>
            {analysis?.status === 'ready' && (() => {
              const hits = summary.filter((d) =>
                d.status === 'red' || d.status === 'yellow')
              return hits.length > 0 && (
                <div className="vqa__investigate">
                  <h4 className="t-label">What to investigate (CV)</h4>
                  <ul>
                    {hits.map((d) => {
                      const tip = analysis.docs?.[d.detector]?.investigate
                      return tip ? (
                        <li key={d.detector}>
                          <strong>{d.detector.replace(/_/g, ' ')}</strong>
                          {' — '}{tip}
                        </li>
                      ) : null
                    })}
                  </ul>
                </div>
              )
            })()}
            {analysis && (
              <p className="vqa__meta">computed in {analysis.computed_in_ms} ms
                {' \u00b7 '}
                <button className="vqa__copy" title="Discard the cached
analysis and run every detector again"
                        onClick={() => setAnalysisNonce((n) => n + 1)}>
                  re-run analysis</button>
              </p>)}
          </section>
          <section>
            <h3 className="t-label">Notes</h3>
            <ul className="vqa__notes">
              {notes.filter((n) => !n.removed).map((n) => {
                const { frame: nf, text } = noteFrame(n.body)
                return (
                  <li key={n.id}>
                    {nf != null && (
                      <button className="vqa__noteframe"
                              onClick={() => setFrame(nf)}>#{nf}</button>)}
                    <span>{text}</span>
                    <span className="vqa__meta"> — {n.by ?? '?'}</span>
                  </li>
                )
              })}
            </ul>
            <textarea className="vqa__notebox" value={noteDraft} rows={5}
                      placeholder={`Note at frame ${frame}…`}
                      onChange={(e) => setNoteDraft(e.target.value)} />
            <button className="adm-btn" disabled={!noteDraft.trim()}
                    onClick={postNote}>Add note</button>
          </section>
      </aside>
      )}
      {!gameId && (
        <p className="vqa__empty">Pick a club and a match to open its
          video beside its tracking.</p>
      )}
    </div>
  )
}
